import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../config/firebaseConfig';
import { checkNicknameDuplicate, checkEmailDuplicate, saveUserInfo, getUserInfo, getUserByEmail } from '../utils/userApi';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 80%;
  max-width: 400px;
  padding: 10px 20px 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow-y: auto;

  @media (max-width: 768px) {
    min-height: 70%;
    width: 80%;
    padding: 5px 20px 10px;
  }
`;

const Title = styled.h2`
  color: white;
  margin-bottom: 20px;
  text-align: center;
  font-size: 1.6rem;
  font-weight: bold;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  color: white;
  font-size: 0.85rem;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    border-color: #ff3369;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--primary-gradient);
  color: white;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.6);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 10px 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 15px 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.3);
  }

  span {
    padding: 0 15px;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #ff3369;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 10px;

  &:hover {
    color: #ff6890;
  }
`;

const ForgotPasswordButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 5px;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ErrorMessage = styled.div`
  color: #ff3369;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 10px;
  padding: 8px;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(255, 107, 107, 0.3);
`;

const SuccessMessage = styled.div`
  color: #4CAF50;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 10px;
  padding: 8px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(76, 175, 80, 0.3);
`;

const UserInfo = styled.div`
  color: white;
  text-align: center;
  margin-bottom: 20px;
`;

const LogoutButton = styled.button`
  padding: 8px 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

function Login({ onLoginSuccess }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [emailStatus, setEmailStatus] = useState(''); // 'checking', 'available', 'duplicate'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [nicknameStatus, setNicknameStatus] = useState(''); // 'checking', 'available', 'duplicate'
  const [isCheckingNickname, setIsCheckingNickname] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showGoogleNickname, setShowGoogleNickname] = useState(false);
  const [googleNickname, setGoogleNickname] = useState('');
  const [isGoogleLoginPending, setIsGoogleLoginPending] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      // Google 로그인 후 닉네임 입력 중이면 메인으로 넘어가지 않음
      if (user && onLoginSuccess && !isGoogleLoginPending) {
        onLoginSuccess(user);
      }
    });

    return () => unsubscribe();
  }, [onLoginSuccess, isGoogleLoginPending]);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isSignUp) {
        // 회원가입 시 닉네임 필수
        if (!nickname.trim()) {
          setError('닉네임을 입력해주세요.');
          setLoading(false);
          return;
        }

        // 닉네임 중복 체크
        if (nicknameStatus !== 'available') {
          setError('사용 가능한 닉네임을 입력해주세요.');
          setLoading(false);
          return;
        }

        // 이메일 중복 체크
        if (emailStatus !== 'available') {
          setError('사용 가능한 이메일을 입력해주세요.');
          setLoading(false);
          return;
        }

        // 비밀번호 확인
        if (password !== confirmPassword) {
          setError('비밀번호가 일치하지 않습니다.');
          setLoading(false);
          return;
        }
        
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // 닉네임 설정
        await updateProfile(userCredential.user, {
          displayName: nickname.trim()
        });
        
        //console.log('회원가입 후 displayName 설정:', userCredential.user.displayName);

        // Firestore에 사용자 정보 저장 (닉네임만 사용)
        await saveUserInfo(userCredential.user.uid, {
          email: userCredential.user.email,
          nickname: nickname.trim(),
          createdAt: new Date()
        });
        
        setSuccess('계정이 성공적으로 생성되었습니다!');
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        setSuccess('로그인 성공!');
      }
    } catch (error) {
      let errorMessage = '로그인 중 오류가 발생했습니다.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = '등록되지 않은 이메일입니다.';
          break;
        case 'auth/wrong-password':
          errorMessage = '잘못된 비밀번호입니다.';
          break;
        case 'auth/email-already-in-use':
          errorMessage = '이미 사용 중인 이메일입니다.';
          break;
        case 'auth/weak-password':
          errorMessage = '비밀번호는 최소 6자 이상이어야 합니다.';
          break;
        case 'auth/invalid-email':
          errorMessage = '유효하지 않은 이메일 형식입니다.';
          break;
        default:
          errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    setIsGoogleLoginPending(true); // Google 로그인 시작

    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      
      // 기존 사용자인지 확인 (이메일로 체크)
      const existingUserByEmail = await getUserByEmail(userCredential.user.email);
      
      if (existingUserByEmail) {
        // 같은 이메일로 이미 가입된 사용자
        alert(`이미 가입된 이메일입니다.\n다른 로그인 방식을 사용해주세요.`);
        setError(`이미 가입된 이메일입니다. 다른 로그인 방식을 사용해주세요.`);
        setLoading(false);
        setIsGoogleLoginPending(false);
        setShowGoogleNickname(false);
        
        // 로그아웃 처리
        await signOut(auth);
        
        // 로그인 모드로 전환
        setIsSignUp(false);
      } else {
        // 새로운 사용자 - 닉네임 설정 필요
        const defaultNickname = userCredential.user.displayName || userCredential.user.email.split('@')[0];
        setGoogleNickname(defaultNickname);
        
        setSuccess(`Google 회원가입 성공! 닉네임을 확인해주세요.`);
        setLoading(false);
        setIsGoogleLoginPending(true);
        setShowGoogleNickname(true);
      }
      
              //console.log('showGoogleNickname 상태:', true);
    } catch (error) {
      let errorMessage = 'Google 로그인 중 오류가 발생했습니다.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = '로그인 창이 닫혔습니다. 다시 시도해주세요.';
      }
      
      setError(errorMessage);
      setLoading(false);
      setIsGoogleLoginPending(false); // 에러 시 상태 초기화
      setShowGoogleNickname(false);
    }
  };

  const handleGoogleNicknameSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // 현재 로그인된 사용자 가져오기
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('로그인 상태를 확인할 수 없습니다.');
        setLoading(false);
        return;
      }

      // 닉네임 유효성 검사
      if (!googleNickname.trim()) {
        setError('닉네임을 입력해주세요.');
        setLoading(false);
        return;
      }

      if (googleNickname.trim().length < 2) {
        setError('닉네임은 최소 2자 이상이어야 합니다.');
        setLoading(false);
        return;
      }

      // 닉네임 중복 체크 (이미 실시간으로 체크했지만 한번 더 확인)
      if (nicknameStatus !== 'available') {
        setError('사용 가능한 닉네임을 입력해주세요.');
        setLoading(false);
        return;
      }
      

              //console.log('googleNickname', googleNickname.trim());
      // 닉네임 설정
      await updateProfile(currentUser, {
        displayName: googleNickname.trim()
      });
      
      // Firestore에 사용자 정보 저장
      await saveUserInfo(currentUser.uid, {
        email: currentUser.email,
        nickname: googleNickname.trim(),
        isGoogleUser: true,
        createdAt: new Date()
      });
      
      setSuccess(`Google 회원가입 완료! 닉네임: ${googleNickname.trim()}`);
      setShowGoogleNickname(false);
      setGoogleNickname('');
      setIsGoogleLoginPending(false); // Google 로그인 완료
      
      // 닉네임 설정 완료 후 메인으로 이동
      if (onLoginSuccess) {
        onLoginSuccess(auth.currentUser);
      }
          } catch (error) {
        //console.error('Google 닉네임 설정 실패:', error);
      setError('닉네임 설정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setSuccess('로그아웃되었습니다.');
    } catch (error) {
      setError('로그아웃 중 오류가 발생했습니다.');
    }
  };

  // 닉네임 중복 체크
  const handleNicknameCheck = async (nickname) => {
    if (!nickname.trim()) {
      setNicknameStatus('');
      return;
    }

    if (nickname.trim().length < 2) {
      setNicknameStatus('duplicate');
      return;
    }

    setIsCheckingNickname(true);
    setNicknameStatus('checking');

    try {
      const result = await checkNicknameDuplicate(nickname.trim());
      
      if (result.isDuplicate) {
        setNicknameStatus('duplicate');
      } else {
        setNicknameStatus('available');
      }
    } catch (error) {
      //console.error('닉네임 중복 체크 실패:', error);
      setNicknameStatus('error');
    } finally {
      setIsCheckingNickname(false);
    }
  };

    // 이메일 중복 체크 (Firestore 사용)
  const handleEmailCheck = async (email) => {
    if (!email.trim()) {
      setEmailStatus('');
      return;
    }

    // 이메일 형식 검사
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmailStatus('duplicate');
      return;
    }

    // 회원가입 모드가 아니면 체크하지 않음
    if (!isSignUp) {
      setEmailStatus('');
      return;
    }

    setEmailStatus('checking');

    try {
      const result = await checkEmailDuplicate(email.trim());
      
      if (result.isDuplicate) {
        setEmailStatus('duplicate');
      } else {
        setEmailStatus('available');
      }
    } catch (error) {
      //console.error('이메일 중복 체크 실패:', error);
      setEmailStatus('error');
    }
  };



  // 닉네임 입력 시 자동 체크 (디바운스)
  useEffect(() => {
    if (!isSignUp) return;

    const timer = setTimeout(() => {
      if (nickname.trim()) {
        handleNicknameCheck(nickname);
      } else {
        setNicknameStatus('');
      }
    }, 500); // 0.5초 후 체크

    return () => clearTimeout(timer);
  }, [nickname, isSignUp]);

  // Google 닉네임 입력 시 자동 체크 (디바운스)
  useEffect(() => {
    if (!showGoogleNickname) return;

    const timer = setTimeout(() => {
      if (googleNickname.trim()) {
        handleNicknameCheck(googleNickname);
      } else {
        setNicknameStatus('');
      }
    }, 500); // 0.5초 후 체크

    return () => clearTimeout(timer);
  }, [googleNickname, showGoogleNickname]);

  // 이메일 입력 시 자동 체크 (디바운스)
  useEffect(() => {
    if (!isSignUp) return;

    const timer = setTimeout(() => {
      if (email.trim()) {
        handleEmailCheck(email);
      } else {
        setEmailStatus('');
      }
    }, 500); // 0.5초 후 체크

    return () => clearTimeout(timer);
  }, [email, isSignUp]);

  // 로그인/회원가입 모드 변경 시 입력 필드 초기화
  useEffect(() => {
    if (!isSignUp) {
      setNickname('');
      setConfirmPassword('');
      setNicknameStatus('');
    }
    // Google 닉네임 폼도 초기화
    setShowGoogleNickname(false);
    setGoogleNickname('');
    setIsGoogleLoginPending(false);
    setEmailStatus('');
  }, [isSignUp]);

  

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setSuccess('비밀번호 재설정 이메일이 발송되었습니다. 이메일을 확인해주세요.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      let errorMessage = '비밀번호 재설정 이메일 발송 중 오류가 발생했습니다.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = '등록되지 않은 이메일입니다.';
          break;
        case 'auth/invalid-email':
          errorMessage = '유효하지 않은 이메일 형식입니다.';
          break;
        default:
          errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Google 로그인 후 닉네임 설정 중일 때는 닉네임 설정 폼만 표시
  if (user && showGoogleNickname && isGoogleLoginPending) {
    return (
      <LoginContainer>
        <Title>닉네임 설정</Title>
                      <Form onSubmit={handleGoogleNicknameSubmit}>
              <InputGroup>
                <Label>닉네임</Label>
                <Input
                  type="text"
                  value={googleNickname}
                  onChange={(e) => setGoogleNickname(e.target.value)}
                  placeholder="사용할 닉네임을 입력하세요"
                  required
                  maxLength={20}
                  style={{
                    borderColor: nicknameStatus === 'available' ? '#4CAF50' : 
                                 nicknameStatus === 'duplicate' ? '#ff3369' : 
                                 nicknameStatus === 'checking' ? '#FFA500' : 
                                 'rgba(255, 255, 255, 0.3)'
                  }}
                />
                {nicknameStatus === 'checking' && (
                  <div style={{ fontSize: '0.8rem', color: '#FFA500', marginTop: '4px' }}>
                    🔍 중복 확인 중...
                  </div>
                )}
                {nicknameStatus === 'available' && (
                  <div style={{ fontSize: '0.8rem', color: '#4CAF50', marginTop: '4px' }}>
                    ✅ 사용 가능한 닉네임입니다
                  </div>
                )}
                {nicknameStatus === 'duplicate' && (
                  <div style={{ fontSize: '0.8rem', color: '#ff3369', marginTop: '4px' }}>
                    ❌ 이미 사용 중인 닉네임입니다
                  </div>
                )}
                {nicknameStatus === 'error' && (
                  <div style={{ fontSize: '0.8rem', color: '#ff3369', marginTop: '4px' }}>
                    ⚠️ 확인 중 오류가 발생했습니다
                  </div>
                )}
              </InputGroup>
              <Button type="submit" disabled={loading || nicknameStatus !== 'available'}>
                {loading ? '처리 중...' : '닉네임 설정 완료'}
              </Button>
            </Form>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {/* {success && <SuccessMessage>{success}</SuccessMessage>} */}
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <Title>{isSignUp ? '회원가입' : '로그인'}</Title>
      <Form onSubmit={handleEmailAuth}>
        {isSignUp && (
          <InputGroup>
            <Label>닉네임</Label>
            <Input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="닉네임을 입력하세요"
              required={isSignUp}
              maxLength={20}
              style={{
                borderColor: nicknameStatus === 'available' ? '#4CAF50' : 
                             nicknameStatus === 'duplicate' ? '#ff3369' : 
                             nicknameStatus === 'checking' ? '#FFA500' : 
                             'rgba(255, 255, 255, 0.3)'
              }}
            />
            {nicknameStatus === 'checking' && (
              <div style={{ fontSize: '0.8rem', color: '#FFA500', marginTop: '4px' }}>
                🔍 중복 확인 중...
              </div>
            )}
            {nicknameStatus === 'available' && (
              <div style={{ fontSize: '0.8rem', color: '#4CAF50', marginTop: '4px' }}>
                ✅ 사용 가능한 닉네임입니다
              </div>
            )}
            {nicknameStatus === 'duplicate' && (
              <div style={{ fontSize: '0.8rem', color: '#ff3369', marginTop: '4px' }}>
                ❌ 이미 사용 중인 닉네임입니다
              </div>
            )}
            {nicknameStatus === 'error' && (
              <div style={{ fontSize: '0.8rem', color: '#ff3369', marginTop: '4px' }}>
                ⚠️ 확인 중 오류가 발생했습니다
              </div>
            )}
          </InputGroup>
        )}
        
        <InputGroup>
          <Label>이메일</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="이메일을 입력하세요"
            required
            style={{
              borderColor: emailStatus === 'available' ? '#4CAF50' : 
                           emailStatus === 'duplicate' ? '#ff3369' : 
                           emailStatus === 'checking' ? '#FFA500' : 
                           'rgba(255, 255, 255, 0.3)'
            }}
          />
          {emailStatus === 'checking' && (
            <div style={{ fontSize: '0.8rem', color: '#FFA500', marginTop: '4px' }}>
              🔍 중복 확인 중...
            </div>
          )}
          {emailStatus === 'available' && (
            <div style={{ fontSize: '0.8rem', color: '#4CAF50', marginTop: '4px' }}>
              ✅ 사용 가능한 이메일입니다
            </div>
          )}
          {emailStatus === 'duplicate' && (
            <div style={{ fontSize: '0.8rem', color: '#ff3369', marginTop: '4px' }}>
              ❌ 이미 사용 중인 이메일입니다
            </div>
          )}
          {emailStatus === 'error' && (
            <div style={{ fontSize: '0.8rem', color: '#ff3369', marginTop: '4px' }}>
              ⚠️ 확인 중 오류가 발생했습니다
            </div>
          )}
        </InputGroup>
        
        <InputGroup>
          <Label>비밀번호</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="비밀번호를 입력하세요"
            required
          />
        </InputGroup>
        
        {isSignUp && (
          <InputGroup>
            <Label>비밀번호 확인</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="비밀번호를 다시 입력하세요"
              required={isSignUp}
              style={{
                borderColor: confirmPassword && password !== confirmPassword ? '#ff3369' : 
                             confirmPassword && password === confirmPassword ? '#4CAF50' : 
                             'rgba(255, 255, 255, 0.3)'
              }}
            />
            {confirmPassword && password !== confirmPassword && (
              <div style={{ fontSize: '0.8rem', color: '#ff3369', marginTop: '4px' }}>
                ❌ 비밀번호가 일치하지 않습니다
              </div>
            )}
            {confirmPassword && password === confirmPassword && (
              <div style={{ fontSize: '0.8rem', color: '#4CAF50', marginTop: '4px' }}>
                ✅ 비밀번호가 일치합니다
              </div>
            )}
          </InputGroup>
        )}
        
        <Button type="submit" disabled={loading}>
          {loading ? '처리 중...' : (isSignUp ? '회원가입' : '로그인')}
        </Button>
      </Form>

      <Divider>
        <span>또는</span>
      </Divider>

      <GoogleButton onClick={handleGoogleSignIn} disabled={loading}>
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Google로 {isSignUp ? '회원가입' : '로그인'}
      </GoogleButton>

      {!isSignUp && (
        <ForgotPasswordButton onClick={() => setShowForgotPassword(!showForgotPassword)}>
          비밀번호를 잊으셨나요?
        </ForgotPasswordButton>
      )}

      {showForgotPassword && (
        <Form onSubmit={handleForgotPassword}>
          <InputGroup>
            <Label>이메일 주소</Label>
            <Input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="가입한 이메일을 입력하세요"
              required
            />
          </InputGroup>
          <Button type="submit" disabled={loading}>
            {loading ? '처리 중...' : '비밀번호 재설정 이메일 발송'}
          </Button>
        </Form>
      )}

      <ToggleButton onClick={() => setIsSignUp(!isSignUp)}>
        {isSignUp ? '이미 계정이 있으신가요? 로그인' : '계정이 없으신가요? 회원가입'}
      </ToggleButton>

      {/* {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>} */}
    </LoginContainer>
  );
}

export default Login; 